// components
import { Icon } from "../Icon/Icon";
import Box from "../../primitives/Box/Box";
import FlexRow from "../../components/Layout/FlexRow/FlexRow";
import FlexColumn from "../../components/Layout/FlexColumn/FlexColumn";
import {
  Body5MediumTypography,
  Body6MediumTypography,
  Body6RegularTypography,
  Caps2SemiboldTypography,
} from "../../primitives/Typography/typographyVariants";
// styles
import {
  OverlayMessage,
  ProductImage,
  StyledActionButton,
} from "./ProductCard.styles";
// types
import { ProductCardProps } from "./ProductCard.types";
import { WishlistButton } from "./ProductCard.styles";
import Tags from "../Tags/Tags";
import { colorPalette } from "../../theme/colors";
import { transformMedia } from "../../utils/constants";

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  disabled,
  experimentType,
  saleTagText = "",
  nudgeText = "",
  onAddToCart = () => {},
  onWishlistClick,
  onProductClick = () => {},
  isWishlisted = false,
  buttonText = "Add to Cart",
  overlayMessage = "",
  isLoading = false,
  descriptionLineClamp = 1,
  hidePrice = false,
}) => {
  const showActionButton = experimentType !== "new_brand";
  const {
    name,
    description,
    mrp = 1,
    price = 1,
    thumbnail_url,
    fallback_image_url,
    subBrandName,
    totalInventory,
  } = product;
  const discountPercentage =
    mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
  const sanitize_price = (num: number): string => {
    if (!num) {
      return "0";
    }
    const number = Number.parseInt(num.toString());
    return number.toLocaleString();
  };
  return (
    <FlexColumn
      borderRadius={8}
      // backgroundColor="background.default.secondary"
      border="0.5px solid"
      borderColor="border.default.default"
      minWidth="154px"
      gap="8px"
      position="relative"
      pb={8}
      height="100%"
      onClick={onProductClick}
    >
      {saleTagText && (
        <Box position="absolute" top={-10} left={0} right={0} zIndex={1}>
          <FlexRow justifyContent="center" alignItems="center">
            <Tags
              variant="primary"
              label={saleTagText}
              color="text.neutral.on-neutral"
              uppercase={true}
            />
          </FlexRow>
        </Box>
      )}
      <FlexColumn position="relative">
        <ProductImage
          src={transformMedia(thumbnail_url)}
          alt={name}
          disabled={disabled}
          placeholder={fallback_image_url}
        />
        {overlayMessage && (
          <OverlayMessage
            width="100%"
            position="absolute"
            top="50%"
            left="50%"
            backgroundColor="background.utilities.overlay"
          >
            <Body5MediumTypography
              textAlign="center"
              color="text.neutral.on-neutral"
            >
              {overlayMessage}
            </Body5MediumTypography>
          </OverlayMessage>
        )}
        {onWishlistClick && (
          <WishlistButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onWishlistClick(e);
            }}
          >
            <Icon
              name={
                isWishlisted ? "fa-solid fa-bookmark" : "fa-regular fa-bookmark"
              }
              size="14"
              height="14"
              width="14"
              cursor="pointer"
              color={
                isWishlisted
                  ? "text.wishlink.default"
                  : "text.wishlink.on-wishlink"
              }
            />
          </WishlistButton>
        )}
        {nudgeText && (
          <FlexRow
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            gap="4px"
            alignItems="center"
            justifyContent="center"
            p="2px 4px"
            backgroundColor="background.warning.default"
          >
            <Icon
              name="fa-solid fa-receipt"
              size="11"
              height="17"
              width="17"
              color="text.default.default"
            />
            <Body6RegularTypography>{nudgeText}</Body6RegularTypography>
          </FlexRow>
        )}
      </FlexColumn>
      <FlexColumn gap="8px" px="8px">
        <FlexColumn gap="2px">
          <Caps2SemiboldTypography
            fontFamily="Lexend"
            color="text.default.secondary"
            lineClamp={1}
            overflow="hidden"
            textOverflow="ellipsis"
            textTransform="uppercase"
          >
            {name}
          </Caps2SemiboldTypography>
          <Body6RegularTypography
            textOverflow="ellipsis"
            lineClamp={descriptionLineClamp}
          >
            {description}
          </Body6RegularTypography>
        </FlexColumn>
        {!hidePrice && (
          <FlexRow gap={4} alignItems="flex-start">
            {mrp > price && (
              <Body5MediumTypography
                textDecoration="line-through"
                color="text.default.secondary"
              >
                &#8377;{sanitize_price(mrp)}
              </Body5MediumTypography>
            )}
            <Body5MediumTypography color="text.default.default">
              &#8377;{sanitize_price(price)}
            </Body5MediumTypography>
            {discountPercentage > 0 && (
              <Body6MediumTypography color="text.utilities.text-on-measurement">
                {`${discountPercentage}% off`}
              </Body6MediumTypography>
            )}
          </FlexRow>
        )}
        {subBrandName && (
          <FlexRow
            gap={5}
            alignItems="center"
            backgroundColor={colorPalette.info[100]}
            borderRadius="4px"
            width="fit-content"
            p="4px 5px"
          >
            <Icon
              name="fa-solid fa-fire"
              size="11px"
              height="11px"
              width="11px"
              color={colorPalette.info[500]}
            />
            <Body6RegularTypography
              color={colorPalette.info[500]}
              fontSize="11px"
              lineHeight="16px"
            >
              {totalInventory || 0} item
              {(totalInventory || 0) > 1 ? "s" : ""} left
            </Body6RegularTypography>
          </FlexRow>
        )}
      </FlexColumn>
      {showActionButton && (
        <FlexColumn mt="auto" px="8px">
          <StyledActionButton
            variant="primary"
            outlined={disabled ? false : true}
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAddToCart(e);
            }}
            leftIcon={
              isLoading ? (
                <Box
                  as="span"
                  style={{
                    display: "inline-block",
                    animation: "spin 1s linear infinite",
                  }}
                >
                  <Icon
                    name="fa-solid fa-spinner"
                    size="14px"
                    height="20px"
                    width="20px"
                    color="inherit"
                  />
                </Box>
              ) : (
                <Icon
                  name={
                    buttonText === "Share Wishlink"
                      ? "fa-solid fa-share"
                      : buttonText === "Copy Wishlink"
                      ? "fa-solid fa-copy"
                      : "fa-sharp fa-solid fa-plus fa-xs"
                  }
                  size="14px"
                  height="18px"
                  width="9px"
                  color="inherit"
                />
              )
            }
          >
            {buttonText}
          </StyledActionButton>
        </FlexColumn>
      )}
    </FlexColumn>
  );
};
